import React from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import type {
  BannerSwapChannelResult,
  BannerSwapChannelResultStatus,
} from "../../../utils/@types";
import { toBannerSwapDate } from "../bannerDashboardUtils";
import { sanitizeBannerWorkerError } from "../bannerErrorUtils";
import { isValid } from "date-fns";

function statusChip(status: BannerSwapChannelResultStatus): React.ReactNode {
  const styles: Record<string, { bg: string; text: string }> = {
    pending: { bg: "#e5e7eb", text: "text-gray-800" },
    running: { bg: "#bfdbfe", text: "text-blue-800" },
    completed: { bg: "#bbf7d0", text: "text-green-800" },
    failed: { bg: "#fecaca", text: "text-red-800" },
    retrying: { bg: "#fde68a", text: "text-amber-900" },
    skipped: { bg: "#f3f4f6", text: "text-gray-600" },
  };
  const s = styles[status] ?? styles.pending;
  return (
    <span
      className={`${s.text} px-2 py-0.5 rounded-full text-xs font-medium capitalize`}
      style={{ backgroundColor: s.bg }}
    >
      {status}
    </span>
  );
}

function formatRetryAt(raw: unknown): string {
  if (raw == null || raw === "") {
    return "—";
  }
  const d = toBannerSwapDate(raw);
  return isValid(d) ? d.toLocaleString() : String(raw);
}

type Props = {
  channelResults?: BannerSwapChannelResult[];
};

const ChannelResultsPanel: React.FC<Props> = ({ channelResults }) => {
  if (!channelResults?.length) {
    return (
      <p className="text-sm text-gray-500 m-0">
        No per-channel execution data (legacy swap or v2 flag off). Queue
        entries created with Banner Swap v2 automation include start/end phase
        outcomes here.
      </p>
    );
  }

  const sorted = [...channelResults].sort((a, b) => {
    const c = a.channelId.localeCompare(b.channelId);
    if (c !== 0) {
      return c;
    }
    return a.phase.localeCompare(b.phase);
  });

  return (
    <div className="w-full">
      <h3 className="text-lg font-semibold mb-2">Per-channel execution</h3>
      <DataTable
        value={sorted}
        size="small"
        showGridlines
        tableStyle={{ minWidth: "40rem" }}
        rows={sorted.length}
        paginator={sorted.length > 12}
        rowsPerPageOptions={[12, 24, 48]}
      >
        <Column field="channelId" header="Channel ID" sortable />
        <Column
          field="phase"
          header="Phase"
          body={(r: BannerSwapChannelResult) => (
            <span className="uppercase text-xs font-semibold">{r.phase}</span>
          )}
          sortable
        />
        <Column
          field="status"
          header="Status"
          body={(r: BannerSwapChannelResult) => statusChip(r.status)}
          sortable
        />
        <Column
          field="attemptCount"
          header="Attempts"
          body={(r: BannerSwapChannelResult) =>
            `${r.attemptCount ?? 0} / ${r.maxAttempts ?? "—"}`
          }
        />
        <Column
          field="nextRetryAt"
          header="Next retry"
          body={(r: BannerSwapChannelResult) => formatRetryAt(r.nextRetryAt)}
        />
        <Column
          field="lastError"
          header="Last error"
          body={(r: BannerSwapChannelResult) => {
            const text = sanitizeBannerWorkerError(r.lastError);
            return (
              <span
                className="text-xs text-gray-700 break-all"
                title={text}
              >
                {text}
              </span>
            );
          }}
        />
        <Column
          field="httpStatus"
          header="HTTP"
          body={(r: BannerSwapChannelResult) => r.httpStatus ?? "—"}
        />
      </DataTable>
    </div>
  );
};

export default ChannelResultsPanel;
